import { PaymentScheduleStage } from '@/ui/components/paymentScheduleStageCards/PaymentScheduleStage';
import {
  ProposalTemplateDepositStageCard,
  ProposalTemplateInstalmentStageCard,
} from '@/ui/components/paymentScheduleStageCards/ProposalTemplatePaymentScheduleStageCards';
import {
  PAYMENT_SCHEDULE_FORM_SCHEMA,
  PaymentScheduleFormValues,
} from '@/ui/components/paymentScheduleStageCards/types';
import { usePaymentScheduleStages } from '@/ui/components/paymentScheduleStageCards/usePaymentScheduleStages';
import { zodResolver } from '@hookform/resolvers/zod';
import { FieldError } from '@payaca/ambrosia';
import { type EditableStoredPaymentSchedule } from '@payaca/helpers/editableStoredPaymentSchedule';
import { FC, useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

type Props = {
  paymentSchedule: EditableStoredPaymentSchedule;
  onChange: (paymentSchedule: EditableStoredPaymentSchedule) => void;
};

/**
 * The template form is a `ValidatedForm`, which renders its own `<form>`, so
 * this keeps the stage cards' react-hook-form state in a `FormProvider` and
 * reports every change up rather than rendering a nested form.
 */
const ProposalTemplatePaymentScheduleEditor: FC<Props> = (props) => {
  const { paymentSchedule, onChange } = props;

  const methods = useForm<PaymentScheduleFormValues>({
    defaultValues: { paymentSchedule },
    resolver: zodResolver(PAYMENT_SCHEDULE_FORM_SCHEMA),
    mode: 'onChange',
    shouldFocusError: false,
  });

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // The template's save button follows the whole schedule's validity, so the
  // cards show every error at once rather than only on fields already edited.
  useEffect(() => {
    void methods.trigger();
    const subscription = methods.watch(() => {
      void methods.trigger();
      onChangeRef.current(methods.getValues('paymentSchedule'));
    });
    return () => subscription.unsubscribe();
  }, [methods]);

  return (
    <FormProvider {...methods}>
      <Stages />
    </FormProvider>
  );
};

const Stages: FC = () => {
  const {
    fields,
    stages,
    allocatedPercentage,
    remainingPercentage,
    addStage,
    removeStage,
    setFirstStageIsDeposit,
    stagesErrorMessage,
  } = usePaymentScheduleStages();

  if (!fields.length) {
    return <PaymentScheduleStage.AddScheduleButton onClick={addStage} />;
  }

  return (
    <div className="@container flex flex-col gap-4">
      <PaymentScheduleStage.Header allocatedPercentage={allocatedPercentage} />
      <div className="flex flex-col gap-4">
        {fields.map((field, index) => {
          const StageCard =
            stages[index]?.type === 'deposit'
              ? ProposalTemplateDepositStageCard
              : ProposalTemplateInstalmentStageCard;
          return (
            <StageCard
              key={field.id}
              index={index}
              remainingPercentage={remainingPercentage}
              onDepositChange={index === 0 ? setFirstStageIsDeposit : undefined}
              onRemove={() => removeStage(index)}
            />
          );
        })}
      </div>
      {stagesErrorMessage && <FieldError messages={[stagesErrorMessage]} />}
      <PaymentScheduleStage.AddStageButton
        remainingPercentage={remainingPercentage}
        onClick={addStage}
      />
      <PaymentScheduleStage.ExplainerField />
    </div>
  );
};

export default ProposalTemplatePaymentScheduleEditor;
